import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWalletAuth } from '../../context/WalletAuthContext';
import { companyAdminAPI } from '../../lib/walletApi';
import { Button } from '../../components/ui/button';
import { Loader2, Eye, EyeOff, ArrowRight, ArrowLeft, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function WalletLogin() {
  const navigate = useNavigate();
  const { login } = useWalletAuth();
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const handleCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const resp = await companyAdminAPI.init2FA(email, password);
      setUserType(resp.user_type);
      setStep('otp');
      setCountdown(60);
      toast.success('Verification code sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value[value.length - 1];
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste.length === 6) {
      setOtp(paste.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const data = await companyAdminAPI.verify2FA(email, code, userType);
      
      // Check if force password reset is required
      if (data.force_password_reset) {
        const redirect = data.user.user_type === 'company_admin' ? '/company-admin' : '/employee-app';
        navigate('/force-password-reset', { 
          state: { token: data.access_token, user: data.user, redirectTo: redirect },
          replace: true 
        });
        toast.info('Please set a new password before continuing');
        return;
      }
      
      login(data.access_token, data.user);
      const redirect = data.user.user_type === 'company_admin' ? '/company-admin' : '/employee-app';
      navigate(redirect);
      toast.success('Welcome back!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await companyAdminAPI.resend2FA(email);
      setCountdown(60);
      toast.success('New code sent');
    } catch { toast.error('Failed to resend'); }
    finally { setResending(false); }
  };

  // Auto-submit when all 6 digits entered
  useEffect(() => {
    if (otp.every(d => d !== '') && step === 'otp') handleVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  return (
    <div className="min-h-screen flex relative overflow-hidden" data-testid="login-page">
      {/* LEFT PANEL */}
      <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=800"
          alt="Modern architecture"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#D64D7A]/80" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <button onClick={() => navigate('/')} className="flex items-center gap-2.5 group" data-testid="login-logo">
            <img src="/pebbles-logo.svg" alt="Pebbles" className="h-8 brightness-0 invert group-hover:scale-105 transition-transform" />
          </button>
          <div className="max-w-md">
            <h2 className="text-5xl font-extrabold text-white leading-[1.15] tracking-tight" style={{ fontFamily: 'Manrope' }}>
              You're in good company
            </h2>
            <p className="text-white/70 text-lg mt-5 leading-relaxed">
              From startups to established enterprises, companies of all sizes trust Pebbles to deliver pay and benefits.
            </p>
          </div>
          <div />
        </div>
      </div>

      {/* GEOMETRIC ACCENT */}
      <div className="hidden lg:block absolute bottom-0 right-0 z-0" style={{ width: 0, height: 0, borderBottom: '280px solid #f59e0b', borderLeft: '320px solid transparent' }} />
      <div className="hidden lg:block absolute bottom-0 right-0 z-0" style={{ width: 0, height: 0, borderBottom: '200px solid #fbbf24', borderLeft: '230px solid transparent' }} />

      {/* RIGHT PANEL */}
      <div className="flex-1 flex flex-col bg-white relative z-10">
        <div className="flex items-center justify-between px-8 pt-6">
          <button onClick={() => navigate('/')} className="lg:hidden flex items-center gap-2" data-testid="login-logo-mobile">
            <img src="/pebbles-logo.svg" alt="Pebbles" className="h-7" />
          </button>
          <div className="hidden lg:block" />
          <a href="mailto:pebblessupport@thcohq.com?subject=Help%20Request%20-%20Pebbles%20Platform" className="text-sm text-[#6b7280] hover:text-[#D64D7A] transition-colors" data-testid="login-help-link">
            Having trouble? <span className="font-semibold text-[#D64D7A]">Get help <ArrowRight className="inline w-3.5 h-3.5" /></span>
          </a>
        </div>

        <div className="flex-1 flex items-center justify-center px-8">
          <div className="w-full max-w-[420px]">

            {step === 'credentials' ? (
              <>
                <h1 className="text-3xl font-extrabold text-[#1a1d1f] mb-2" style={{ fontFamily: 'Manrope' }} data-testid="login-heading">
                  Welcome back!
                </h1>
                <p className="text-[#6b7280] mb-8">Sign in to your Pebbles account</p>

                <form onSubmit={handleCredentials} className="space-y-5">
                  <div>
                    <input
                      type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email Address" required
                      className="w-full h-[52px] px-4 rounded-lg border border-[#d1d5db] bg-white text-[15px] text-[#1a1d1f] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all"
                      data-testid="login-email"
                    />
                  </div>
                  <div>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                        placeholder="Password" required
                        className="w-full h-[52px] px-4 pr-12 rounded-lg border border-[#d1d5db] bg-white text-[15px] text-[#1a1d1f] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all"
                        data-testid="login-password"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9ca3af] hover:text-[#1a1d1f] transition-colors" data-testid="toggle-password">
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <div className="flex justify-end mt-2">
                      <button type="button" onClick={() => navigate('/forgot-password')} className="text-sm font-semibold text-[#D64D7A] hover:underline" data-testid="forgot-password-link">
                        Forgot password?
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} data-testid="login-submit-btn"
                    className="w-full h-[52px] rounded-lg bg-[#D64D7A] hover:bg-[#c4406b] text-white text-base font-semibold shadow-[0_4px_16px_rgba(214,77,122,0.3)]">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue'}
                  </Button>
                </form>
              </>
            ) : (
              <div className="space-y-6">
                <button onClick={() => { setStep('credentials'); setOtp(['','','','','','']); }}
                  className="flex items-center gap-1.5 text-sm text-[#6b7280] hover:text-[#1a1d1f] transition-colors" data-testid="2fa-back-btn">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>

                <div className="text-center space-y-2">
                  <div className="w-14 h-14 rounded-xl bg-[#D64D7A]/10 flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-7 h-7 text-[#D64D7A]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#1a1d1f]" data-testid="otp-heading">Check your email</h2>
                  <p className="text-[#6b7280] text-sm">
                    We sent a 6-digit code to <span className="text-[#1a1d1f] font-medium">{email}</span>
                  </p>
                </div>

                <form onSubmit={handleVerify} className="space-y-5">
                  <div className="flex justify-center gap-2.5" onPaste={handleOtpPaste}>
                    {otp.map((digit, i) => (
                      <input key={i} ref={el => otpRefs.current[i] = el} type="text" inputMode="numeric"
                        value={digit} onChange={e => handleOtpChange(i, e.target.value)}
                        onKeyDown={e => handleOtpKeyDown(i, e)} maxLength={1}
                        data-testid={`otp-input-${i}`}
                        className="w-12 h-14 text-center text-xl font-bold rounded-lg border border-[#d1d5db] bg-white text-[#1a1d1f] focus:outline-none focus:ring-2 focus:ring-[#D64D7A]/30 focus:border-[#D64D7A] transition-all"
                      />
                    ))}
                  </div>

                  <Button type="submit" disabled={loading || otp.some(d => !d)} data-testid="otp-verify-btn"
                    className="w-full h-[52px] rounded-lg bg-[#D64D7A] hover:bg-[#c4406b] text-white text-base font-semibold shadow-[0_4px_16px_rgba(214,77,122,0.3)]">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Sign In'}
                  </Button>
                </form>

                <div className="text-center">
                  {countdown > 0 ? (
                    <p className="text-sm text-[#6b7280]">Resend code in <span className="text-[#1a1d1f] font-medium">{countdown}s</span></p>
                  ) : (
                    <button onClick={handleResend} disabled={resending} data-testid="resend-otp-btn"
                      className="text-sm text-[#D64D7A] hover:text-[#c4406b] font-medium flex items-center gap-1.5 mx-auto transition-colors">
                      {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      Resend code
                    </button>
                  )}
                </div>
              </div>
            )}

            <p className="mt-8 text-center text-[12px] text-[#9ca3af]">
              By signing in, you agree to our{' '}
              <a href="#" className="text-[#D64D7A] hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="#" className="text-[#D64D7A] hover:underline">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
